import { DateTime } from 'luxon'
import { type Checklist as TChecklistTemplate } from '@/api/client/models/Checklist'
import { v4 as uuidv4 } from 'uuid'
import {
  type ChecklistModule as TChecklistModule,
  type ChecklistInput as TChecklistResultInput,
} from '@/api/client'
import { ApiError } from '@/api/client'
import { DocumentationService } from '@/api/client'
import { useAuthStore } from '@/stores/AuthStore'
import FileManager from '@/classes/manager/FileManager'
import type { Sendable } from '@/interfaces/Sendable'
import { type File } from '@/types/file'
import { type Error } from '@/types/error'
import { report } from '@/classes/manager/ExceptionReportingManager'
import { useChecklistStore } from '@/stores/ChecklistStore'

type TChecklistData = {
  name: string
  description: string
  project_id: number
  employee_id: number
  readonly result_id: string
  finished: boolean
  finish_date?: string | undefined
  checklist_id: number
  checklist_modules: TChecklistModule[]
  checklist_inputs: TAnyChecklistInput[]
  transmitted: boolean
  created_at: string | undefined
  notification_emails: string[]
  updated_at: string | null
  trashed_files: File[]
  project_label_ids: number[]
}

type TDehydratedChecklist = {
  data: TChecklistData
  type: 'checklist'
  meta: {
    transmitted: boolean
    transmitted_at: string
    error_log: Error[]
  }
}

type TInput = {
  checklist_module_id: number
}
type TTextfieldInput = TInput & {
  type: 'textfield'
  data: { element: string }
}
type TChecklistInput = TInput & {
  type: 'checkbox'
  data: { checked: boolean; id: number }[]
}
type TScaleInput = TInput & {
  type: 'radiobox'
  data: { value_result: number }
}
type TSignatureInput = TInput & {
  type: 'signaturefield'
  data: {
    date: string
    name: string
    location: string
    unassigned_uploaded_file_id: number | null
    file?: (File & { strokes: object[] }) | null
  }
}

type TImageInput = TInput & {
  type: 'photofield'
  data: {
    comment: string
    unassigned_uploaded_file_id: number | null
    file?: File | null
  }[]
}

type TNumberInput = TInput & {
  type: 'numberfield'
  data: { value_result: string }
}

type TAnyChecklistInput =
  | TTextfieldInput
  | TChecklistInput
  | TScaleInput
  | TSignatureInput
  | TImageInput
  | TNumberInput

type TImageModule = TChecklistModule & { content: { element: string } }
type TSignatureModule = TChecklistModule & { content: { element: string } }

class Checklist implements Sendable {
  readonly type = 'checklist'
  meta: {
    transmitted: boolean
    transmitted_at: string
    error_log: Error[]
  }

  data: TChecklistData
  static readonly MODULE_TYPES = {
    TEXTFIELD: 'textfield',
    CHECKLIST: 'checkbox',
    SCALE: 'radiobox',
    SIGNATURE: 'signaturefield',
    IMAGE: 'photofield',
    NUMBER: 'numberfield',
  } as const
  constructor(
    checklistData: TChecklistData,
    meta = {
      transmitted: false,
      transmitted_at: '',
      error_log: [] as Error[],
    },
  ) {
    this.data = checklistData
    this.populateInputs()
    this.meta = meta
  }

  private populateInputs() {
    if (
      !this.data.checklist_inputs ||
      this.data.checklist_inputs.length !== this.data.checklist_modules.length
    ) {
      this.data.checklist_inputs = []
      for (const module of this.data.checklist_modules) {
        this.data.checklist_inputs.push(this.createChecklistInput(module))
      }
    }
  }
  private createChecklistInput(module: TChecklistModule): TAnyChecklistInput {
    switch (module.type) {
      case Checklist.MODULE_TYPES.TEXTFIELD:
        return {
          type: Checklist.MODULE_TYPES.TEXTFIELD,
          checklist_module_id: module.checklist_module_id,
          data: { element: '' },
        }
      case Checklist.MODULE_TYPES.CHECKLIST:
        return {
          type: Checklist.MODULE_TYPES.CHECKLIST,
          checklist_module_id: module.checklist_module_id,
          data: [
            ...((module.content &&
              module.content.map((item: { checked: boolean; id: number }) => ({
                checked: false,
                id: item.id,
              }))) ||
              []),
          ],
        }
      case Checklist.MODULE_TYPES.SCALE:
        return {
          type: Checklist.MODULE_TYPES.SCALE,
          checklist_module_id: module.checklist_module_id,

          data: {
            value_result:
              // TODO: add user defined type guards to fix
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              parseInt(module.content.value_start),
          },
        }
      case Checklist.MODULE_TYPES.SIGNATURE:
        return {
          type: Checklist.MODULE_TYPES.SIGNATURE,
          checklist_module_id: module.checklist_module_id,

          data: {
            date: '',
            name: '',
            location: '',
            unassigned_uploaded_file_id: null,
            file: null,
          },
        }
      case Checklist.MODULE_TYPES.IMAGE:
        return {
          type: 'photofield',
          checklist_module_id: module.checklist_module_id,
          data: [],
        }
      case Checklist.MODULE_TYPES.NUMBER:
        return {
          type: Checklist.MODULE_TYPES.NUMBER,
          checklist_module_id: module.checklist_module_id,
          data: { value_result: '' },
        }
      default:
        throw new Error('Unknown module type')
    }
  }

  async send(
    ignoreMissingFiles?: boolean,
    handleProgress?: (completed: number, total: number) => void,
  ): Promise<{ error: false | Error }> {
    console.log('Checklist: Sending checklist', this.data)
    this.data.finished = this.data.finished ?? true
    this.data.finish_date = this.data.finish_date ?? DateTime.utc().toISO()
    await this.uploadFiles(handleProgress)
    if (!this.allFilesAreUploaded() && !ignoreMissingFiles) {
      return {
        error: {
          error_message: 'not all files were uploaded',
          error_code: 'uploadError',
        },
      }
    }
    const checklistInputs = this.data.checklist_inputs
      ? this.data.checklist_inputs
      : []
    const result = {
      checklist_id: this.data.checklist_id,
      project_id: this.data.project_id,
      employee_id: this.data.employee_id,
      result_id: this.data.result_id,
      finished: this.data.finished,
      finish_date: this.data.finish_date,
      checklist_inputs: this.transformFileInputsForSending(checklistInputs),
      notification_emails: this.data.notification_emails,
      project_label_ids: this.data.project_label_ids,
    }
    try {
      await DocumentationService.storeChecklist(result)
      // only now can we remove from ChecklistStore
      handleProgress?.(1, 1)
      return { error: false }
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 401)
        await useAuthStore().logout()
      return {
        error: {
          error_message: JSON.stringify(e),
          error_code: 'Unable to send checklist',
        },
      }
    }
  }
  private async uploadFiles(
    handleProgress?: (completed: number, total: number) => void,
  ): Promise<{ success: boolean }> {
    const signatures = this.getSignatureInputs()
    const imageInputs = this.getImageInputs()
    const numberOfItemsToSend =
      signatures.length + imageInputs.reduce((c, i) => c + i.data.length, 0) + 1
    let numberOfSentItems = 0
    for (const signature of signatures) {
      if (signature.data.file && !signature.data.unassigned_uploaded_file_id) {
        if (
          !signature.data.file ||
          !signature.data.file.webPath ||
          (signature.data.file.webPath &&
            signature.data.file.webPath.length === 0)
        )
          throw new Error(
            'Checklist: uploadFiles(): Signature does not have FilePath',
          )
        try {
          const response = await FileManager.upload(
            signature.data.file.webPath,
            this.data.employee_id,
          )
          if (!response?.data.unassigned_uploaded_file_id) {
            report(
              new Error(
                'checklist file upload failed with response: ' +
                  JSON.stringify(response),
              ),
            )
            continue
          }
          signature.data.unassigned_uploaded_file_id =
            response?.data?.unassigned_uploaded_file_id
          numberOfSentItems++
          handleProgress?.(numberOfSentItems, numberOfItemsToSend)
        } catch (e) {
          console.error(e)
        }
      }
    }

    for (const imageInput of imageInputs) {
      for (const image of imageInput.data) {
        if (image.file && !image.unassigned_uploaded_file_id) {
          if (
            !image.file ||
            !image.file.webPath ||
            (image.file.webPath && image.file.webPath.length === 0)
          )
            throw new Error(
              'Checklist: uploadFiles(): Image does not have WepPathPath',
            )
          try {
            const response = await FileManager.upload(
              image.file?.webPath || image.file?.path || '',
              this.data.employee_id,
            )
            console.log('Uploading image', response)
            if (!response?.data.unassigned_uploaded_file_id) {
              report(
                new Error(
                  'Checklist: Image Upload Response missing unassigned_uploaded_file_id' +
                    JSON.stringify(response),
                ),
              )
              continue
            }
            image.unassigned_uploaded_file_id =
              response?.data?.unassigned_uploaded_file_id
            numberOfSentItems++
            handleProgress?.(numberOfSentItems, numberOfItemsToSend)
          } catch (e) {
            console.error(e)
          }
        }
      }
    }
    return { success: true }
  }

  isReady() {
    return true
  }

  private transformFileInputsForSending(
    inputs: TAnyChecklistInput[],
  ): TChecklistResultInput[] {
    return inputs.map((input) => {
      switch (input.type) {
        case Checklist.MODULE_TYPES.SIGNATURE:
          // signatures live as base64 in memory => trashing unnececessary
          delete input.data.file
          return input
        case Checklist.MODULE_TYPES.IMAGE:
          for (const image of input.data) {
            if (image.file) this.data.trashed_files.push(image.file)
            delete image.file
          }
          return input
        default:
          return input
      }
    }) as TChecklistResultInput[]
  }
  private getSignatureInputs(): TSignatureInput[] {
    return this.data.checklist_inputs.filter(
      (i) => i.type === Checklist.MODULE_TYPES.SIGNATURE,
    )
  }
  private getImageInputs(): TImageInput[] {
    return this.data.checklist_inputs.filter(
      (i) => i.type === Checklist.MODULE_TYPES.IMAGE,
    )
  }
  private allFilesAreUploaded(): boolean {
    return (
      this.getSignatureInputs().every((i) => {
        const v =
          !i.data.file || (i.data.file && i.data.unassigned_uploaded_file_id)
        return v
      }) &&
      this.getImageInputs().every((i) =>
        i.data.every((j) => {
          const v = j.file && j.unassigned_uploaded_file_id
          return v
        }),
      )
    )
  }
  cleanup() {
    useChecklistStore().removeChecklist(this.data.result_id)
    for (const file of this.data.trashed_files) {
      if (file.path) {
        console.log('Checklist: cleanup(): Deleting file', file.path)
        void FileManager.delete(file.path)
      }
    }
  }

  toJSON(): TDehydratedChecklist {
    console.log('DEHYDRATING CHECKLIST')
    return {
      data: this.data,
      meta: this.meta,
      type: this.type,
    }
  }

  static fromJSON(dehydratedChecklist: TDehydratedChecklist): Checklist {
    console.log('HYRDRATING CHECKLIST', dehydratedChecklist)
    return new Checklist(dehydratedChecklist.data, dehydratedChecklist.meta)
  }

  static createFromTemplate(
    template: TChecklistTemplate,
    project_id: number,
    employee_id: number,
  ): Checklist {
    return new Checklist({
      name: template.name,
      description: template.description || '',
      project_id: project_id,
      employee_id: employee_id,
      result_id: uuidv4(),
      finished: false,
      finish_date: undefined,
      checklist_id: template.checklist_id,
      checklist_modules:
        template.modules?.sort((a, b) => a.sequence - b.sequence) || [],
      checklist_inputs: [],
      transmitted: false,
      created_at: DateTime.utc().toISO(),
      notification_emails: [],
      updated_at: DateTime.utc().toISO(),
      trashed_files: [],
      project_label_ids: [],
    })
  }
}

export {
  Checklist,
  type TChecklistData,
  type TDehydratedChecklist,
  type TImageInput,
  type TSignatureInput,
  type TImageModule,
  type TSignatureModule,
}
